/* =====================================================================
 * Document Store — 문서 전역 상태 관리 (Zustand)
 * ===================================================================== */

import { create } from 'zustand';
import { v4 as uuid } from 'uuid';
import type { DocumentData, DocumentMeta } from '@auw/workspace-utils';

/* -----------------------------------------------------------------
 * `JSONContent` mirrors the shape produced by Tiptap's `editor.getJSON()`.
 * We inline a structural copy here (rather than importing from
 * `@tiptap/react`) so @auw/stores stays editor-agnostic and doesn't
 * drag Tiptap into B2B bundles that don't ship the document mode.
 * ----------------------------------------------------------------- */
interface JSONContent {
  type?: string;
  attrs?: Record<string, unknown>;
  content?: JSONContent[];
  marks?: Array<{ type: string; attrs?: Record<string, unknown>; [key: string]: unknown }>;
  text?: string;
  [key: string]: unknown;
}
import { getActiveAccountId } from '@auw/api-client';
import { activeTenantKey as tenantKey } from '@auw/tenant';

/* ------------------------------------------------------------------ */
/* State Interface                                                     */
/* ------------------------------------------------------------------ */

interface DocumentState {
  /* data */
  documents: Record<string, DocumentData>;
  documentOrder: string[];
  activeDocumentId: string | null;

  /* AI */
  isAIStreaming: boolean;
  aiStreamContent: string;

  /* actions – documents */
  createDocument: (title?: string) => string;
  ensureDocument: (id: string, title?: string) => void;
  deleteDocument: (id: string) => void;
  renameDocument: (id: string, title: string) => void;
  setActiveDocument: (id: string | null) => void;
  updateContent: (id: string, content: JSONContent) => void;
  updateTitle: (id: string, title: string) => void;

  /* actions – AI */
  setAIStreaming: (v: boolean) => void;
  appendAIStreamContent: (delta: string) => void;
  clearAIStreamContent: () => void;

  /* getters */
  activeDocument: () => DocumentData | null;
  getDocumentList: () => DocumentMeta[];
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const STORAGE_KEY_DOCS = tenantKey('documents');
const STORAGE_KEY_ORDER = tenantKey('document_order');

function getScopedStorageKeys(): { docs: string; order: string } {
  const accountId = getActiveAccountId();
  const suffix = accountId ? accountId : 'anonymous';
  return {
    docs: `${STORAGE_KEY_DOCS}:${suffix}`,
    order: `${STORAGE_KEY_ORDER}:${suffix}`,
  };
}

function makeDocument(title?: string, fixedId?: string): DocumentData {
  const now = new Date().toISOString();
  return {
    id: fixedId ?? uuid(),
    title: title || '',
    content: {
      type: 'doc',
      content: [{ type: 'paragraph' }],
    },
    createdAt: now,
    updatedAt: now,
  };
}

function saveDocuments(
  documents: Record<string, DocumentData>,
  order: string[],
) {
  try {
    const keys = getScopedStorageKeys();
    localStorage.setItem(keys.docs, JSON.stringify(documents));
    localStorage.setItem(keys.order, JSON.stringify(order));
  } catch {
    /* noop */
  }
}

function loadDocuments(): {
  documents: Record<string, DocumentData>;
  order: string[];
} {
  try {
    const keys = getScopedStorageKeys();
    const rawDocs = localStorage.getItem(keys.docs);
    const rawOrder = localStorage.getItem(keys.order);
    if (rawDocs) {
      return {
        documents: JSON.parse(rawDocs),
        order: rawOrder ? JSON.parse(rawOrder) : [],
      };
    }
  } catch {
    /* noop */
  }
  return { documents: {}, order: [] };
}

/* ------------------------------------------------------------------ */
/* Store                                                               */
/* ------------------------------------------------------------------ */

const persisted = loadDocuments();

export const useDocumentStore = create<DocumentState>((set, get) => ({
  /* ── initial state ── */
  documents: persisted.documents,
  documentOrder: persisted.order,
  activeDocumentId: persisted.order[0] ?? null,
  isAIStreaming: false,
  aiStreamContent: '',

  /* ── actions – documents ── */

  createDocument: (title?: string) => {
    const doc = makeDocument(title);
    set((s) => {
      const documents = { ...s.documents, [doc.id]: doc };
      const documentOrder = [doc.id, ...s.documentOrder];
      saveDocuments(documents, documentOrder);
      return {
        documents,
        documentOrder,
        activeDocumentId: doc.id,
      };
    });
    return doc.id;
  },

  ensureDocument: (id: string, title?: string) => {
    set((s) => {
      const existing = s.documents[id];
      if (existing) return s;
      const doc = makeDocument(title, id);
      const documents = { ...s.documents, [doc.id]: doc };
      const documentOrder = [doc.id, ...s.documentOrder.filter((d) => d !== doc.id)];
      saveDocuments(documents, documentOrder);
      return { documents, documentOrder };
    });
  },

  deleteDocument: (id: string) => {
    set((s) => {
      const { [id]: _, ...rest } = s.documents;
      const documentOrder = s.documentOrder.filter((d) => d !== id);
      const activeDocumentId =
        s.activeDocumentId === id
          ? documentOrder[0] ?? null
          : s.activeDocumentId;
      saveDocuments(rest, documentOrder);
      return { documents: rest, documentOrder, activeDocumentId };
    });
  },

  renameDocument: (id: string, title: string) => {
    set((s) => {
      const doc = s.documents[id];
      if (!doc) return s;
      const updated = { ...doc, title, updatedAt: new Date().toISOString() };
      const documents = { ...s.documents, [id]: updated };
      saveDocuments(documents, s.documentOrder);
      return { documents };
    });
  },

  setActiveDocument: (id: string | null) => {
    set({ activeDocumentId: id });
  },

  updateContent: (id: string, content: JSONContent) => {
    set((s) => {
      const doc = s.documents[id];
      if (!doc) return s;
      const updated = { ...doc, content, updatedAt: new Date().toISOString() };
      const documents = { ...s.documents, [id]: updated };
      saveDocuments(documents, s.documentOrder);
      return { documents };
    });

    // 백엔드에도 콘텐츠 저장 (디바운싱은 에디터에서 처리)
    import('@auw/api-client').then(({ syncSaveContent }) => {
      syncSaveContent(id, 'tiptap_json', content).catch(() => {});
    });
  },

  updateTitle: (id: string, title: string) => {
    set((s) => {
      const doc = s.documents[id];
      if (!doc) return s;
      const updated = { ...doc, title, updatedAt: new Date().toISOString() };
      const documents = { ...s.documents, [id]: updated };
      saveDocuments(documents, s.documentOrder);
      return { documents };
    });
  },

  /* ── actions – AI ── */

  setAIStreaming: (v: boolean) => {
    set({ isAIStreaming: v });
  },

  appendAIStreamContent: (delta: string) => {
    set((s) => ({ aiStreamContent: s.aiStreamContent + delta }));
  },

  clearAIStreamContent: () => {
    set({ aiStreamContent: '', isAIStreaming: false });
  },

  /* ── getters ── */

  activeDocument: () => {
    const { documents, activeDocumentId } = get();
    if (!activeDocumentId) return null;
    return documents[activeDocumentId] ?? null;
  },

  getDocumentList: () => {
    const { documents, documentOrder } = get();
    return documentOrder
      .map((id) => {
        const d = documents[id];
        if (!d) return null;
        return {
          id: d.id,
          title: d.title,
          icon: d.icon,
          coverImage: d.coverImage,
          createdAt: d.createdAt,
          updatedAt: d.updatedAt,
        } as DocumentMeta;
      })
      .filter(Boolean) as DocumentMeta[];
  },
}));
